import { useState, useEffect } from "react";
import axios from "axios";
import { Clock, UtensilsCrossed, Cake, Moon } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WEDDING_DATE = new Date("2026-05-09T16:30:00");

function Countdown() {
  const [tl, setTl] = useState(() => calcTimeLeft());
  function calcTimeLeft() {
    const diff = WEDDING_DATE - new Date();
    if (diff <= 0) return { d: 0, h: 0, m: 0, s: 0, done: true };
    return {
      d: Math.floor(diff / 86400000),
      h: Math.floor((diff / 3600000) % 24),
      m: Math.floor((diff / 60000) % 60),
      s: Math.floor((diff / 1000) % 60),
      done: false,
    };
  }
  useEffect(() => {
    const i = setInterval(() => setTl(calcTimeLeft()), 1000);
    return () => clearInterval(i);
  }, []);

  if (tl.done) {
    return (
      <div className="text-center" data-testid="countdown-done">
        <p className="font-serif text-xl md:text-2xl text-wedding-text italic">
          Danas je naš dan
        </p>
      </div>
    );
  }

  const box = (val, label) => (
    <div className="flex flex-col items-center">
      <div className="min-w-[52px] md:min-w-[64px] bg-wedding-bg/60 border border-wedding-gold/25 px-2 py-3 md:px-3 md:py-4">
        <p className="font-serif text-2xl md:text-3xl text-wedding-text tabular-nums">
          {String(val).padStart(2, "0")}
        </p>
      </div>
      <p className="font-sans text-[9px] md:text-[10px] uppercase tracking-[0.2em] text-wedding-text-secondary mt-2">
        {label}
      </p>
    </div>
  );

  return (
    <div className="relative" data-testid="countdown">
      <p className="text-center font-sans text-[10px] md:text-xs uppercase tracking-[0.3em] text-wedding-gold mb-4">
        Vidimo se za
      </p>
      <div className="flex items-start justify-center gap-2 md:gap-3">
        {box(tl.d, "Dana")}
        {box(tl.h, "Sati")}
        {box(tl.m, "Min")}
        {box(tl.s, "Sek")}
      </div>
    </div>
  );
}

export default function LandingPage() {
  useEffect(() => {
    axios.post(`${API}/track`).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-wedding-bg">
      {/* Hero Image Banner */}
      <div className="w-full h-48 md:h-64 overflow-hidden relative" data-testid="hero-image">
        <img 
          src="https://images.unsplash.com/photo-1554110838-816383ce7956?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwxfHxpc2xhbWljJTIwYXJ0JTIwd2hpdGUlMjBnb2xkJTIwbWluaW1hbCUyMGFic3RyYWN0fGVufDB8fHx8MTc3NjIzNjQ3OHww&ixlib=rb-4.1.0&q=85"
          alt="Mosque"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-wedding-bg"></div>
      </div>

      {/* Hero Section */}
      <section className="flex flex-col items-center justify-center text-center px-6 pt-6 pb-4" data-testid="hero-section">
        
        {/* Script heading */}
        <p className="font-script text-4xl md:text-5xl lg:text-6xl text-wedding-text/70 mb-6 opacity-0 animate-fade-in-up">
          Šerijatsko vjenčanje
        </p>

        {/* Names - Premium Gold Hover */}
        <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-semibold tracking-[0.15em] uppercase mb-5 shimmer-gold cursor-default" style={{animation: "fadeInUp 0.8s ease forwards 0.15s"}}>
          Amel & Meliha
        </h1>

        {/* Thin divider */}
        <div className="w-12 h-px bg-wedding-text/20 my-4 opacity-0 animate-fade-in animate-delay-300"></div>

        {/* Date + Location side by side */}
        <div className="flex items-center gap-6 md:gap-10 opacity-0 animate-fade-in-up animate-delay-300">
          <div className="text-center">
            <p className="font-serif text-2xl md:text-3xl text-wedding-text font-normal">09.05.</p>
            <p className="font-serif text-2xl md:text-3xl text-wedding-text font-normal">2026</p>
          </div>
          <div className="w-px h-14 bg-wedding-text/20"></div>
          <div className="text-center">
            <p className="font-serif text-lg md:text-xl text-wedding-text">Džemat Zurich</p>
            <p className="font-sans text-xs text-wedding-text-secondary tracking-wide">Grabenstrasse 7, 8952 Schlieren</p>
          </div>
        </div>

        {/* Confirmation deadline - registrations closed */}
        <p className="mt-10 font-sans text-xs uppercase tracking-[0.2em] text-wedding-gold opacity-0 animate-fade-in-up animate-delay-400">
          Prijave su zatvorene · Hvala svima
        </p>

        {/* CTA pill button - scrolls to thank-you */}
        <a 
          href="#rsvp" 
          className="mt-4 px-8 py-2.5 bg-wedding-text text-white text-xs uppercase tracking-[0.2em] font-sans hover:bg-wedding-text/80 transition-colors opacity-0 animate-fade-in-up animate-delay-500"
          data-testid="rsvp-cta"
        >
          Poruka za goste
        </a>
      </section>

      {/* Invitation Text Section - removed, moved content up */}

      {/* Timeline Section */}
      <section className="py-4 md:py-8 px-6" data-testid="timeline-section">
        <div className="max-w-sm mx-auto">
          <div className="text-center mb-5">
            <p className="font-script text-2xl text-wedding-text/60 mb-1">Plan</p>
            <h2 className="font-serif text-xl md:text-2xl text-wedding-text uppercase tracking-[0.1em]">
              Vjenčanja
            </h2>
            <div className="w-8 h-px bg-wedding-gold mx-auto mt-3"></div>
          </div>

          <div className="space-y-2 pl-4 sm:pl-8">
            {[
              { icon: Clock, time: "16:30", label: "Okupljanje gostiju" },
              { icon: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5"><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2z"/><path d="M8 12c0-2 1.5-4 4-4s4 2 4 4-1.5 4-4 4-4-2-4-4z"/></svg>, time: "16:45", label: "Šerijatsko vjenčanje" },
              { icon: UtensilsCrossed, time: "18:00", label: "Večera" },
              { icon: Cake, time: "21:00", label: "Torta" },
              { icon: Moon, time: "Nakon toga", label: "Druženje" },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-4 py-2">
                <div className="w-8 h-8 rounded-full bg-white border border-wedding-gold/30 flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-3.5 h-3.5 text-wedding-gold" />
                </div>
                <p className="font-serif text-base text-wedding-text font-medium w-20 flex-shrink-0">{item.time}</p>
                <p className="font-sans text-sm text-wedding-text-secondary font-light">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Thank You Section (registrations closed) */}
      <section id="rsvp" className="py-6 md:py-10 px-6" data-testid="thankyou-section">
        <div className="bg-white p-8 md:p-12 max-w-md mx-auto shadow-sm">

          {/* Elegant Islamic ornamental flourish */}
          <div className="flex justify-center mb-8" data-testid="ornament">
            <svg viewBox="0 0 260 40" className="w-full max-w-[300px] h-auto" fill="none" stroke="currentColor" strokeWidth="0.9" strokeLinecap="round" strokeLinejoin="round" style={{ color: "var(--wedding-gold, #C4A574)" }}>
              {/* Left side: connecting line + 3 small Islamic stars */}
              <path d="M6 20 H 96" />
              {/* small 8-point star at x=30 */}
              <g transform="translate(30 20) scale(0.45)">
                <path d="M0 -10 L2 -2 L10 0 L2 2 L0 10 L-2 2 L-10 0 L-2 -2 Z" fill="currentColor" fillOpacity="0.2" />
                <path d="M0 -10 L2 -2 L10 0 L2 2 L0 10 L-2 2 L-10 0 L-2 -2 Z" />
                <path d="M-7 -7 L1.4 -1.4 L7 -7 L1.4 1.4 L7 7 L-1.4 1.4 L-7 7 L-1.4 -1.4 Z" opacity="0.6" />
              </g>
              {/* small star at x=55 */}
              <g transform="translate(55 20) scale(0.35)">
                <path d="M0 -10 L2 -2 L10 0 L2 2 L0 10 L-2 2 L-10 0 L-2 -2 Z" fill="currentColor" fillOpacity="0.25" />
                <path d="M0 -10 L2 -2 L10 0 L2 2 L0 10 L-2 2 L-10 0 L-2 -2 Z" />
              </g>
              {/* small star at x=78 */}
              <g transform="translate(78 20) scale(0.28)">
                <path d="M0 -10 L2 -2 L10 0 L2 2 L0 10 L-2 2 L-10 0 L-2 -2 Z" fill="currentColor" fillOpacity="0.3" />
                <path d="M0 -10 L2 -2 L10 0 L2 2 L0 10 L-2 2 L-10 0 L-2 -2 Z" />
              </g>

              {/* Center crescent moon */}
              <g transform="translate(130 20)">
                <path d="M -2 -11 A 11 11 0 1 0 -2 11 A 8.5 8.5 0 1 1 -2 -11 Z" fill="currentColor" fillOpacity="0.18" />
                <path d="M -2 -11 A 11 11 0 1 0 -2 11 A 8.5 8.5 0 1 1 -2 -11 Z" />
                <circle cx="6" cy="0" r="0.9" fill="currentColor" />
              </g>

              {/* Right side: 3 small stars + connecting line (mirror) */}
              <g transform="translate(182 20) scale(0.28)">
                <path d="M0 -10 L2 -2 L10 0 L2 2 L0 10 L-2 2 L-10 0 L-2 -2 Z" fill="currentColor" fillOpacity="0.3" />
                <path d="M0 -10 L2 -2 L10 0 L2 2 L0 10 L-2 2 L-10 0 L-2 -2 Z" />
              </g>
              <g transform="translate(205 20) scale(0.35)">
                <path d="M0 -10 L2 -2 L10 0 L2 2 L0 10 L-2 2 L-10 0 L-2 -2 Z" fill="currentColor" fillOpacity="0.25" />
                <path d="M0 -10 L2 -2 L10 0 L2 2 L0 10 L-2 2 L-10 0 L-2 -2 Z" />
              </g>
              <g transform="translate(230 20) scale(0.45)">
                <path d="M0 -10 L2 -2 L10 0 L2 2 L0 10 L-2 2 L-10 0 L-2 -2 Z" fill="currentColor" fillOpacity="0.2" />
                <path d="M0 -10 L2 -2 L10 0 L2 2 L0 10 L-2 2 L-10 0 L-2 -2 Z" />
                <path d="M-7 -7 L1.4 -1.4 L7 -7 L1.4 1.4 L7 7 L-1.4 1.4 L-7 7 L-1.4 -1.4 Z" opacity="0.6" />
              </g>
              <path d="M164 20 H 254" />
            </svg>
          </div>

          {/* Message - premium shimmer animation */}
          <p className="text-center font-serif text-base md:text-lg leading-relaxed mb-8 thankyou-shimmer" data-testid="thankyou-message">
            Hvala svima koji su potvrdili dolazak.<br />
            Radujemo se vašem dolasku.
          </p>

          {/* Countdown */}
          <Countdown />

          {/* Signature */}
          <p className="text-center font-script text-2xl md:text-3xl text-wedding-text mt-8">
            Amel &amp; Meliha
          </p>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-8 md:py-12 text-center max-w-lg mx-auto px-6" data-testid="quote-section">
        <blockquote className="font-serif text-lg md:text-xl text-wedding-text italic leading-relaxed mb-3">
          "I On je sjedinio srca njihova."
        </blockquote>
        <cite className="font-sans text-[10px] uppercase tracking-[0.25em] text-wedding-text-secondary/60 not-italic">
          Surah Al-Anfal 8:63
        </cite>
      </section>

      {/* Navigation links */}
      <div className="pb-10 flex items-center justify-center gap-6">
        <a
          href="/"
          className="font-sans text-[10px] text-wedding-text-secondary/30 hover:text-wedding-gold transition-colors duration-300 tracking-widest uppercase"
          data-testid="home-link"
        >
          Home
        </a>
        <div className="w-px h-3 bg-wedding-text-secondary/20"></div>
        <a
          href="/admin"
          className="font-sans text-[10px] text-wedding-text-secondary/30 hover:text-wedding-gold transition-colors duration-300 tracking-widest uppercase"
          data-testid="admin-link"
        >
          Admin
        </a>
        <div className="w-px h-3 bg-wedding-text-secondary/20"></div>
        <a
          href="/igra"
          className="font-sans text-[10px] text-wedding-text-secondary/30 hover:text-wedding-gold transition-colors duration-300 tracking-widest uppercase"
          data-testid="game-link"
        >
          Game
        </a>
      </div>
    </div>
  );
}
